import { useMutation, useQuery, useTRPC } from "@luckymenu/api-client/react";
import type { InvoicePdfLink, PlatformInvoice } from "@luckymenu/core";
import { useCallback, useMemo, useState } from "react";
import { nextDueDate } from "../subscription/view";

/** An unpaid invoice with what is still owed on it. */
export type DueInvoice = PlatformInvoice & { owedPaise: number };

/**
 * What the restaurant owes LuckyMenu, for the owner (`subscription.view`).
 * Works while the restaurant is suspended, so the paused screen can show
 * what to pay and the invoices to download (P10-T11). `enabled` is false for
 * other roles, which the server refuses anyway.
 */
export function useMySubscription(enabled = true) {
	const trpc = useTRPC();
	const query = useQuery({
		...trpc.staff.platformBilling.getMySubscription.queryOptions(),
		enabled,
	});
	const pdf = useMutation(trpc.staff.platformBilling.getInvoicePdf.mutationOptions());
	const [downloading, setDownloading] = useState<string | null>(null);
	const [failed, setFailed] = useState(false);

	const due = useMemo<DueInvoice[]>(
		() =>
			(query.data?.invoices ?? [])
				.filter((i) => i.status === "issued")
				.map((i) => ({ ...i, owedPaise: i.totalPaise - i.paidPaise }))
				.sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
		[query.data],
	);

	/** A five minute link to the invoice's PDF, for the app to open; null when it failed. */
	const download = useCallback(
		async (invoiceId: string): Promise<InvoicePdfLink | null> => {
			setDownloading(invoiceId);
			setFailed(false);
			try {
				return await pdf.mutateAsync({ invoiceId });
			} catch {
				setFailed(true);
				return null;
			} finally {
				setDownloading(null);
			}
		},
		[pdf],
	);

	return {
		status: !enabled
			? ("off" as const)
			: query.data
				? ("ready" as const)
				: query.isError
					? ("error" as const)
					: ("loading" as const),
		retry: () => void query.refetch(),
		subscription: query.data?.subscription ?? null,
		/** Whom to pay: our legal name and UPI ID. */
		payTo: query.data?.payTo ?? null,
		/** Unpaid invoices, the earliest due first. */
		due,
		/** When the next payment is due: an unpaid invoice's due date, or the end of the paid period or trial. */
		nextDue: nextDueDate(query.data?.subscription ?? null, due),
		/** Every invoice that is not void, the newest period first. */
		invoices: query.data?.invoices ?? [],
		download,
		/** The invoice whose PDF is being fetched. */
		downloading,
		/** The last download failed. */
		failed,
	};
}
